import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';

import { showUnauthorizedToast } from 'src/utils/message';
import { DocumentDownloadIdKey, DocumentDownloadRequestParams } from '../types/document';
import { headers, timeOut } from '../utils/constants';

import { ApiResponseResult } from 'src/types/shared';
import { constants } from 'src/utils/settings';
import { downloadMessages } from '../shared/constants';
import { track } from '../utils/telemetry';

// const getToken = () => '';

const axiosRequestConfig: AxiosRequestConfig = {
  baseURL: constants.api.url,
  timeout: timeOut,
  headers,
};

const axiosRequestConfigWithAuthentication: AxiosRequestConfig = {
  baseURL: constants.api.url,
  timeout: timeOut,
  headers,
  withCredentials:true,
};

export const axiosClient = axios.create(axiosRequestConfig);


export const axiosClientWithAuthentication = axios.create(axiosRequestConfigWithAuthentication);

// TOREFACTOR - move auth credentials out of code
axiosClientWithAuthentication.interceptors.request.use(
  config => {
    // if (config.baseURL && config.headers && !config.headers.Authorization) {
    //   const token = getToken();
    //   if (token) {
    //     config.headers.Authorization = `Bearer ${token}`;
    //   }
    // }
  //   if(config.baseURL && config.headers){
  //   config.withCredentials = true;
  // }

    if (config.timeout === undefined || config.timeout === 0) {
      return config;
    }

    const source = axios.CancelToken.source();

    setTimeout(() => {
      source.cancel(
        `Cancelled request. Took longer than ${config.timeout}ms to get complete response.`,
      );
    }, config.timeout);

    // If caller configures cancelToken, preserve cancelToken behaviour.
    if (config.cancelToken) {
      config.cancelToken.promise.then(cancel => {
        source.cancel(cancel.message);
      });
    }
    return { ...config, cancelToken: source.token };
  },
  error => Promise.reject(error),
);

const handleResponseError = (error: unknown) => {
  if (!axios.isAxiosError(error)) {
    return Promise.reject(error);
  }
  const { response, message = '' } = error;
  if (message === `Cancelled request. Took longer than ${timeOut}ms to get complete response.`) {
    const abortController = new AbortController();
    abortController.abort();
  }
  if (response && response.status === 401) {
    console.error('api - 401', error);
    showUnauthorizedToast(error.config?.url);
  }
  return Promise.reject(error);
};
axiosClient.interceptors.response.use(undefined, handleResponseError);
axiosClientWithAuthentication.interceptors.response.use(undefined, handleResponseError);

export type AxiosRequestOptions = Record<string, unknown>;

/**
 * Direct Axios request with typing enforced.
 * TODO: response should be raw. create a base request,
 *  or update the hard-coded expectation of an `ApiResponseResult` typed result (`data.result`)?
 * @param {AxiosRequestConfig} config: request param object
 * @param {boolean} useAuthentication: header, defaults to true
 * @returns a typed {} Promise
 */
export async function axiosRequest<T>(config: AxiosRequestConfig<T>, useAuthentication = false) {
  const axiosInstance: AxiosInstance =
    useAuthentication === true ? axiosClientWithAuthentication : axiosClient;
  try {
    const response = await axiosInstance.request<ApiResponseResult<T>>(config);
    return response?.data?.result;
  } catch (error: any) {
    track('API_ERROR', {
      error,
      method: config?.method || 'GET',
      axiosRequestConfig: config?.url,
      config,
    });
    // TOREFACTOR - standardize error handling and reporting, failing silently, etc.
    console.error(`axiosRequest ERROR: \n${error}`);
    throw new Error(error);
  }
}

/**
 * Helper fn for authenticated POST requests.
 * @param config: AxiosRequestConfig
 * @returns a typed Promise
 */
export async function axiosPostRequest<T>(config: Omit<AxiosRequestConfig, 'method'>) {
  return axiosRequest<T>({ ...config, method: 'POST' });
}

/**
 * Helper fn for authenticated requests.
 * @param config
 * @returns a typed Promise
 */
export async function axiosRequestAuthenticated<T>(config: AxiosRequestConfig) {
  return axiosRequest<T>(config, true);
}

/**
 * Helper fn for authenticated POST requests.
 * @param config: AxiosRequestConfig
 * @returns a typed Promise
 */
export async function axiosPostAuthenticated<T>(config: AxiosRequestConfig) {
  return axiosRequestAuthenticated<T>({ ...config, method: 'POST' });
}

/**
 * Helper fn for authenticated download requests.
 * @param config
 */
export async function axiosRequestDownloadAuthenticated<T>(config: AxiosRequestConfig) {
  return axiosRequest<T>(config, true);
}

/**
 * Typed, authenticated Axios download GET request.
 * This also adds, programmatically clicks, then removes an `<a href.../>` anchor.
 * Auth token is sent via the header rather than appended to the URL.
 * @param requestParams: DocumentDownloadRequestParams
 */
export async function downloadRequestAuthenticated(requestParams: DocumentDownloadRequestParams) {
  const { baseUrl, docIdParams: data, requestUrl } = requestParams;
  const docIdKeys = Object.keys(data);
  const queryString = docIdKeys
    .map(key => {
      const value = data[key as DocumentDownloadIdKey] || '';
      return encodeURIComponent(key) + '=' + encodeURIComponent(value);
    })
    .join('&');

  const downloadUrl = `${baseUrl}${requestUrl}?${queryString}`;

  // Request the document.
  try {
    const response = await axios.request({
      url: downloadUrl,
      method: 'GET',
      // headers: {
      //   Authorization: `Bearer ${getToken()}`,
      // },
      responseType: 'blob',
    });

    const responseData = response.data;

    let fileName;

    // Parse the response header content-disposition for the actual file name.
    const contentDisposition = response?.headers['content-disposition'] || '';
    if (contentDisposition !== '') {
      const contentDispositionAttributes = contentDisposition.split(';');
      fileName = contentDispositionAttributes.find(
        (cont: string) => cont.trim().indexOf('filename=') >= 0,
      );
      fileName = fileName?.replace('filename=', '').replace(/"/g, '').trim();
    }

    // Throw an error if the fileName can't be determined.
    if (!fileName) {
      throw new Error(downloadMessages.fileNameNotFound);
    }

    // Create a blob URL representing the data.
    const blob = new Blob([responseData], { type: response?.data?.type || '' });
    const windowUrl = window.URL.createObjectURL(blob);

    // Attach the blob URL to anchor element with download attribute
    const anchor = document.createElement('a');
    anchor.setAttribute('href', windowUrl);
    anchor.setAttribute('target', '_blank');
    anchor.setAttribute('download', fileName);
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    window.URL.revokeObjectURL(windowUrl);
  } catch (error) {
    console.error(error);
  }
}

const downloadFileWithAuthentication = (requestUrl = '', data = {} as any, baseUrl = '') => {
  try {
    const docIdKeys = Object.keys(data);
    const queryString = docIdKeys
      .map((key: string) => {
        return encodeURIComponent(key) + '=' + encodeURIComponent(data[key]);
      })
      .join('&');

    const link = document.createElement('a');
    // link.href = `${baseUrl}${requestUrl}?${queryString}${queryString && '&'}tk=${getToken()}`;
    link.href = `${baseUrl}${requestUrl}?${queryString}`;

    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } catch (error) {
    track('API_ERROR', { error, requestUrl, data, baseUrl });
    return undefined;
  }
};
